import { useState } from 'react'
import { Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Activity, Book, ApiChild, PortfolioResponse } from '@/lib/api'
import { educationalAreaLabel } from '@/lib/constants'
import { useFamilyStore } from '@/stores/familyStore'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatDateShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-birch-200 border-t-birch-600" />
    </div>
  )
}

// ── ImageModal ────────────────────────────────────────────────────────────────

function ImageModal({
  images,
  initialIndex,
  onClose,
}: {
  images: string[]
  initialIndex: number
  onClose: () => void
}) {
  const [idx, setIdx] = useState(initialIndex)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="relative max-h-full max-w-xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[idx]}
          alt={`Photo ${idx + 1}`}
          className="max-h-[80vh] w-full rounded-xl object-contain shadow-2xl"
        />
        {images.length > 1 && (
          <>
            <button
              onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gray-700 shadow-md hover:bg-white text-lg"
            >
              &#8249;
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gray-700 shadow-md hover:bg-white text-lg"
            >
              &#8250;
            </button>
          </>
        )}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-600 shadow-md hover:bg-gray-100"
          aria-label="Close"
        >
          &#10005;
        </button>
      </div>
    </div>
  )
}

// ── Activity list for a single area ──────────────────────────────────────────

function AreaSection({
  area,
  activities,
  areaSummary,
}: {
  area: string
  activities: Activity[]
  areaSummary: string | undefined
}) {
  const sorted = [...activities].sort((a, b) =>
    (a.date ?? '').localeCompare(b.date ?? ''),
  )
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null)

  return (
    <section id={`area-${area}`} className="print:break-before-page">
      <h2 className="mb-2 border-b border-birch-200 pb-1 text-lg font-bold text-birch-800">
        {educationalAreaLabel(area)}
      </h2>

      {areaSummary && (
        <p className="mb-3 text-sm text-gray-600 italic">{areaSummary}</p>
      )}

      <ul className="space-y-2">
        {sorted.map((activity) => {
          const photos = activity.mediaAttachments ?? []
          return (
            <li key={activity.id} className="text-sm">
              <div className="flex gap-2">
                {activity.date && (
                  <span className="shrink-0 text-gray-400 w-32">
                    {formatDate(activity.date)}
                  </span>
                )}
                <span className="text-gray-800">{activity.description}</span>
              </div>
              {activity.notes && (
                <p className="ml-32 mt-0.5 text-xs italic text-gray-500">
                  "{activity.notes}"
                </p>
              )}
              {photos.length > 0 && (
                <div className="ml-32 mt-1.5 flex flex-wrap gap-1.5">
                  {photos.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={`Photo ${i + 1}`}
                      className="h-16 w-16 cursor-zoom-in rounded object-cover print:h-48 print:w-48"
                      onClick={() => setLightbox({ images: photos, index: i })}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {lightbox && (
        <ImageModal
          images={lightbox.images}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  )
}

// ── Books grid ───────────────────────────────────────────────────────────────

function BooksSection({ books }: { books: Book[] }) {
  const sorted = [...books].sort((a, b) =>
    (a.dateRead ?? '').localeCompare(b.dateRead ?? ''),
  )

  return (
    <section id="reading-list" className="print:break-before-page">
      <h2 className="mb-3 border-b border-birch-200 pb-1 text-lg font-bold text-birch-800">
        Reading List
      </h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {sorted.map((book) => (
          <div
            key={book.id}
            className="flex gap-2 rounded-lg border border-gray-100 bg-white p-2 shadow-sm print:border print:shadow-none"
          >
            {book.mediaAttachments?.length > 0 ? (
              <img
                src={book.mediaAttachments[0]}
                alt={book.title}
                className="h-20 w-14 shrink-0 rounded object-cover print:h-60 print:w-44"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            ) : (
              <div className="flex h-20 w-14 shrink-0 items-center justify-center rounded bg-birch-100 print:h-60 print:w-44">
                <svg
                  className="h-5 w-5 text-birch-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
                  />
                </svg>
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 leading-tight">
                {book.title}
              </p>
              {book.author && (
                <p className="text-xs text-gray-500">{book.author}</p>
              )}
              {formatDate(book.dateRead) && (
                <p className="mt-1 text-[10px] text-gray-400">
                  {formatDate(book.dateRead)}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Portfolio content (rendered once data is loaded) ─────────────────────────

function PortfolioContent({ data }: { data: PortfolioResponse }) {
  const { child, dateRange, activitiesByArea, books, summary, generatedAt } = data

  // Build ordered list of areas that actually have activities
  const areas = Object.keys(activitiesByArea).filter(
    (a) => activitiesByArea[a].length > 0,
  )

  const ageText = child.age
    ? `age ${child.age.years}${child.age.months > 0 ? ` and ${child.age.months} month${child.age.months !== 1 ? 's' : ''}` : ''}`
    : null

  return (
    <div className="mx-auto max-w-3xl space-y-8 print:max-w-none">
      {/* Header */}
      <header className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">
          {child.firstName}
          {ageText && (
            <span className="ml-2 text-xl font-normal text-gray-500">
              {ageText}
            </span>
          )}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Portfolio: {formatDateShort(dateRange.startDate)} &ndash;{' '}
          {formatDateShort(dateRange.endDate)}
        </p>
        {generatedAt && (
          <p className="mt-0.5 text-xs text-gray-400">
            Generated {formatDateTime(generatedAt)}
          </p>
        )}
      </header>

      {/* Overall summary */}
      {summary?.overall && (
        <section className="rounded-lg border border-birch-200 bg-birch-50 p-4 print:border print:bg-white">
          <h2 className="mb-1 text-sm font-semibold text-birch-700 uppercase tracking-wide">
            Summary
          </h2>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            {summary.overall}
          </p>
        </section>
      )}

      {/* Table of Contents */}
      <nav className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm print:shadow-none">
        <h2 className="mb-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Contents
        </h2>
        <ul className="columns-2 gap-x-6 text-sm">
          {areas.map((area) => (
            <li key={area} className="mb-1">
              <a
                href={`#area-${area}`}
                className="text-birch-600 hover:underline print:text-gray-800 print:no-underline"
              >
                {educationalAreaLabel(area)}
              </a>
              <span className="ml-1 text-gray-400">
                ({activitiesByArea[area].length})
              </span>
            </li>
          ))}
          {books.length > 0 && (
            <li className="mb-1">
              <a
                href="#reading-list"
                className="text-birch-600 hover:underline print:text-gray-800 print:no-underline"
              >
                Reading List
              </a>
              <span className="ml-1 text-gray-400">({books.length})</span>
            </li>
          )}
        </ul>
      </nav>

      {/* Educational area sections */}
      {areas.map((area) => (
        <AreaSection
          key={area}
          area={area}
          activities={activitiesByArea[area]}
          areaSummary={summary?.byArea?.[area]}
        />
      ))}

      {/* Books */}
      {books.length > 0 && <BooksSection books={books} />}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function PortfolioPage() {
  const { activeChildId } = useFamilyStore()
  const queryClient = useQueryClient()

  const today = new Date().toISOString().split('T')[0]
  const sixMonthsAgo = new Date(Date.now() - 183 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [genStartDate, setGenStartDate] = useState(sixMonthsAgo)
  const [genEndDate, setGenEndDate] = useState(today)
  const [featuredOnly, setFeaturedOnly] = useState(true)

  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['me'],
    queryFn: api.me.get,
  })

  const familyId = profile?.familyId ?? null

  const { data: children = [], isLoading: loadingChildren } = useQuery<
    ApiChild[]
  >({
    queryKey: ['children', familyId],
    queryFn: () => api.children.list(familyId!),
    enabled: !!familyId,
  })

  const activeChild: ApiChild | null =
    children.find((c) => c.id === activeChildId) ?? children[0] ?? null
  const childId = activeChild?.id ?? null

  const {
    data: portfolio,
    isLoading: loadingPortfolio,
  } = useQuery<PortfolioResponse | null>({
    queryKey: ['portfolio', familyId, childId],
    queryFn: () => api.portfolio.get(familyId!, childId!),
    enabled: !!familyId && !!childId,
  })

  const generateMutation = useMutation({
    mutationFn: () =>
      api.portfolio.generate(
        familyId!,
        childId!,
        genStartDate || undefined,
        genEndDate || undefined,
      ),
    onSuccess: () => {
      setShowGenerateModal(false)
      queryClient.invalidateQueries({ queryKey: ['portfolio', familyId, childId] })
    },
  })

  const isLoading = loadingProfile || (!!familyId && loadingChildren)

  // ── No family ────────────────────────────────────────────────────────────
  if (!isLoading && !familyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-500">Set up your family first.</p>
          <Link
            to="/family"
            className="mt-3 inline-block rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700"
          >
            Go to Family
          </Link>
        </div>
      </div>
    )
  }

  // ── No children ──────────────────────────────────────────────────────────
  if (!isLoading && familyId && !activeChild) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-500">
            Add a student in Family to view their portfolio.
          </p>
          <Link
            to="/family"
            className="mt-3 inline-block rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700"
          >
            Go to Family
          </Link>
        </div>
      </div>
    )
  }

  const isFirstGenerate = portfolio === null
  const generateModal = showGenerateModal ? (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={() => setShowGenerateModal(false)}
    >
      <div
        className="w-full max-w-sm rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900">
          {isFirstGenerate ? 'Generate Portfolio' : 'Regenerate Portfolio'}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Choose the date range for activities and books to include.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Start Date</label>
            <input
              type="date"
              value={genStartDate}
              onChange={(e) => setGenStartDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">End Date</label>
            <input
              type="date"
              value={genEndDate}
              onChange={(e) => setGenEndDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>
        </div>
        {generateMutation.isError && (
          <p className="mt-2 text-sm text-red-600">
            {(generateMutation.error as Error).message}
          </p>
        )}
        <div className="mt-5 flex gap-2">
          <button
            onClick={() => setShowGenerateModal(false)}
            className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-60"
          >
            {generateMutation.isPending && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {generateMutation.isPending
              ? isFirstGenerate ? 'Generating…' : 'Regenerating…'
              : isFirstGenerate ? 'Generate' : 'Regenerate'}
          </button>
        </div>
      </div>
    </div>
  ) : null

  // ── Portfolio not yet generated (404) ─────────────────────────────────────
  if (!isLoading && !loadingPortfolio && portfolio === null) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
        {activeChild && (
          <p className="mt-0.5 text-sm text-gray-500">{activeChild.firstName}</p>
        )}
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-700 font-medium">No portfolio has been generated yet.</p>
          <p className="mt-1 text-sm text-gray-500">
            Generate a portfolio to create a summary of all activities and books.
          </p>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700"
          >
            Generate Portfolio
          </button>
        </div>
        {generateModal}
      </div>
    )
  }

  return (
    <div className="space-y-6 print:space-y-4">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
          {activeChild && (
            <p className="mt-0.5 text-sm text-gray-500">
              {activeChild.firstName}
            </p>
          )}
        </div>
        {portfolio && (
          <div className="flex flex-col items-end gap-1.5">
            {portfolio.generatedAt && (
              <p className="text-xs text-gray-400">
                Generated {formatDateTime(portfolio.generatedAt)}
              </p>
            )}
            <div className="flex items-center gap-2">
              <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={featuredOnly}
                  onChange={(e) => setFeaturedOnly(e.target.checked)}
                  className="rounded border-gray-300 text-birch-600 focus:ring-birch-500"
                />
                Featured only
              </label>
              <button
                onClick={() => setShowGenerateModal(true)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                Regenerate
              </button>
              <button
                onClick={() => window.print()}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              >
                Print
              </button>
            </div>
          </div>
        )}
      </div>

      {(isLoading || loadingPortfolio) && <Spinner />}

      {!isLoading && !loadingPortfolio && portfolio && (
        <PortfolioContent
          data={
            featuredOnly
              ? {
                  ...portfolio,
                  activitiesByArea: Object.fromEntries(
                    Object.entries(portfolio.activitiesByArea).map(([area, acts]) => [
                      area,
                      acts.filter((a) => a.isFeatured),
                    ]),
                  ),
                  books: portfolio.books,
                }
              : portfolio
          }
        />
      )}

      {generateModal}
    </div>
  )
}
